import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sunrise, Laugh, Swords, Sun, Sparkles, Loader2, Lock, RotateCcw, Send, Share2, Quote, ArrowRight } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AiThinking } from "@/components/AiThinking";
import { CinematicVideo } from "@/components/CinematicVideo";

const MAX = 400;
const TROIS_AGES_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/sub4i57n_gemini_generated_video_94d9e890.mp4";

const T = {
  fr: {
    eyebrow: "les trois âges de la vérité", title: "Regarde ton idée traverser le temps",
    subtitle: "Inspiré de Schopenhauer : toute vérité est d'abord raillée, puis violemment combattue, puis acceptée comme une évidence. Soumets une idée et vois son parcours.",
    placeholder: "Une idée, une conviction, une invention (400 caractères max)…",
    go: "Retracer son parcours", loading: "Le temps se déroule…", error: "Le récit n'a pas pu s'écrire. Réessaie.",
    suggest: "Ou choisis une idée qui a marqué l'histoire :",
    presets: ["La Terre tourne autour du Soleil.", "Les femmes devraient avoir le droit de voter.", "On peut apprendre sans école.", "Se laver les mains sauve des vies."],
    ideaLabel: "L'idée", act1: "La Raillerie", act2: "Le Combat", act3: "L'Évidence",
    closingLabel: "Le mot de la fin",
    deepen: "Approfondir avec L'Élan", again: "Une autre idée", card: "Carte à partager", cardError: "Le partage a échoué.",
    deepenPrompt: (idea) => `J'ai exploré « ${idea} » à travers les Trois Âges de la Vérité (raillée, combattue, puis évidente). Aide-moi à aller plus loin : les faits historiques marquants de ce parcours, et une idée d'aujourd'hui qui en est encore au premier âge.`,
    locked: "Les Trois Âges de la Vérité sont réservés aux abonnés.",
    lockedDesc: "Passe à L'Élan ou au Cocoon pour voir tes idées traverser le temps.", lockedCta: "Découvrir les forfaits",
  },
  en: {
    eyebrow: "the three ages of truth", title: "Watch your idea travel through time",
    subtitle: "Inspired by Schopenhauer: every truth is first ridiculed, then violently opposed, then accepted as self-evident. Submit an idea and watch its journey.",
    placeholder: "An idea, a conviction, an invention (400 characters max)…",
    go: "Trace its journey", loading: "Time unfolds…", error: "The story couldn't be written. Try again.",
    suggest: "Or pick an idea that shaped history:",
    presets: ["The Earth revolves around the Sun.", "Women should have the right to vote.", "You can learn without school.", "Washing hands saves lives."],
    ideaLabel: "The idea", act1: "Ridicule", act2: "The Fight", act3: "Self-Evidence",
    closingLabel: "A final word",
    deepen: "Go deeper with L'Élan", again: "Another idea", card: "Shareable card", cardError: "Sharing failed.",
    deepenPrompt: (idea) => `I explored "${idea}" through the Three Ages of Truth (ridiculed, fought, then obvious). Help me go further: the key historical facts of this journey, and an idea of today still in its first age.`,
    locked: "The Three Ages of Truth are for subscribers.",
    lockedDesc: "Upgrade to L'Élan or the Cocoon to watch your ideas travel through time.", lockedCta: "See the plans",
  },
  es: {
    eyebrow: "las tres edades de la verdad", title: "Mira tu idea viajar en el tiempo",
    subtitle: "Inspirado en Schopenhauer: toda verdad primero se ridiculiza, luego se combate con violencia, luego se acepta como evidente. Presenta una idea y observa su recorrido.",
    placeholder: "Una idea, una convicción, un invento (400 caracteres máx.)…",
    go: "Trazar su recorrido", loading: "El tiempo se despliega…", error: "El relato no pudo escribirse. Inténtalo de nuevo.",
    suggest: "O elige una idea que marcó la historia:",
    presets: ["La Tierra gira alrededor del Sol.", "Las mujeres deberían tener derecho al voto.", "Se puede aprender sin escuela.", "Lavarse las manos salva vidas."],
    ideaLabel: "La idea", act1: "La Burla", act2: "El Combate", act3: "La Evidencia",
    closingLabel: "La última palabra",
    deepen: "Profundizar con L'Élan", again: "Otra idea", card: "Tarjeta para compartir", cardError: "Falló al compartir.",
    deepenPrompt: (idea) => `Exploré «${idea}» a través de las Tres Edades de la Verdad (ridiculizada, combatida, luego evidente). Ayúdame a ir más lejos: los hechos históricos clave de este recorrido, y una idea de hoy que sigue en su primera edad.`,
    locked: "Las Tres Edades de la Verdad son solo para suscriptores.",
    lockedDesc: "Pásate a L'Élan o al Cocoon para ver tus ideas viajar en el tiempo.", lockedCta: "Ver los planes",
  },
  nl: {
    eyebrow: "de drie tijdperken van de waarheid", title: "Zie je idee door de tijd reizen",
    subtitle: "Geïnspireerd op Schopenhauer: elke waarheid wordt eerst belachelijk gemaakt, dan hevig bestreden, dan aanvaard als vanzelfsprekend. Dien een idee in en bekijk zijn reis.",
    placeholder: "Een idee, een overtuiging, een uitvinding (max. 400 tekens)…",
    go: "Volg zijn reis", loading: "De tijd ontvouwt zich…", error: "Het verhaal kon niet worden geschreven. Probeer opnieuw.",
    suggest: "Of kies een idee dat de geschiedenis tekende:",
    presets: ["De aarde draait om de zon.", "Vrouwen zouden stemrecht moeten hebben.", "Je kunt leren zonder school.", "Handen wassen redt levens."],
    ideaLabel: "Het idee", act1: "De Spot", act2: "De Strijd", act3: "De Vanzelfsprekendheid",
    closingLabel: "Een laatste woord",
    deepen: "Verdiep met L'Élan", again: "Een ander idee", card: "Deelbare kaart", cardError: "Delen mislukt.",
    deepenPrompt: (idea) => `Ik verkende '${idea}' via de Drie Tijdperken van de Waarheid (bespot, bestreden, dan vanzelfsprekend). Help me verder: de belangrijkste historische feiten van deze reis, en een idee van vandaag dat nog in zijn eerste tijdperk zit.`,
    locked: "De Drie Tijdperken van de Waarheid zijn voor abonnees.",
    lockedDesc: "Ga over naar L'Élan of de Cocoon om je ideeën door de tijd te zien reizen.", lockedCta: "Bekijk de abonnementen",
  },
};

export default function TroisAges({ onExplore }) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lng = (i18n.language || "fr").slice(0, 2);
  const tv = T[lng] || T.fr;
  const [idea, setIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [sharing, setSharing] = useState(false);

  const run = async (value) => {
    const q = ((value ?? idea) || "").trim();
    if (!q || loading) return;
    setIdea(q); setLoading(true); setData(null); setLocked(false);
    try {
      const { data: res } = await api.post("/verites/stages", { idea: q, lang: lng });
      setData(res);
    } catch (e) {
      if (e.response?.status === 402) setLocked(true);
      else toast.error(formatApiErrorDetail(e.response?.data?.detail) || tv.error);
    } finally { setLoading(false); }
  };

  const reset = () => { setData(null); setIdea(""); };
  const deepen = () => { if (data && onExplore) onExplore(tv.deepenPrompt(data.idea)); };

  const shareCard = async () => {
    if (!data || sharing) return;
    setSharing(true);
    try {
      const { data: blob } = await api.post("/verites/card", {
        idea: data.idea, ridicule: data.ridicule, combat: data.combat, evidence: data.evidence,
        lang: lng, eyebrow: tv.eyebrow, labels: [tv.act1, tv.act2, tv.act3],
      }, { responseType: "blob" });
      const file = new File([blob], "cocoon-trois-ages.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) await navigator.share({ files: [file], title: tv.title });
      else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "cocoon-trois-ages.png"; a.click(); URL.revokeObjectURL(url);
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error(tv.cardError); } finally { setSharing(false); }
  };

  const acts = data ? [
    { key: "ridicule", label: tv.act1, Icon: Laugh, cls: "border-slate-400/30 bg-slate-400/[0.05]", name: "text-slate-300", chip: "bg-slate-400/15 text-slate-300", text: data.ridicule },
    { key: "combat", label: tv.act2, Icon: Swords, cls: "border-red-400/30 bg-red-400/[0.06]", name: "text-red-300", chip: "bg-red-400/15 text-red-300", text: data.combat },
    { key: "evidence", label: tv.act3, Icon: Sun, cls: "border-primary/30 bg-primary/[0.07]", name: "text-primary", chip: "bg-primary/15 text-primary", text: data.evidence },
  ] : [];

  return (
    <div className="h-full overflow-y-auto scroll-thin">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="text-center mb-8">
          <span className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.3em] text-primary/70 mb-3"><Sunrise size={14} /> {tv.eyebrow}</span>
          <h1 className="font-display text-3xl sm:text-4xl text-gradient-orange leading-tight mb-3" data-testid="tav-title">{tv.title}</h1>
          <p className="text-foreground/60 leading-relaxed max-w-xl mx-auto text-sm sm:text-base">{tv.subtitle}</p>
        </div>

        {/* cinématique en fondu */}
        <div className="mb-8">
          <CinematicVideo fade src={TROIS_AGES_VIDEO} testId="trois-ages-cinematic-video" caption={tv.subtitle} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); run(); }} className="rounded-2xl bg-white/[0.03] border border-white/12 p-3 mb-3" data-testid="tav-form">
          <textarea value={idea} onChange={(e) => setIdea(e.target.value.slice(0, MAX))} placeholder={tv.placeholder} rows={2} data-testid="tav-input"
            className="w-full bg-transparent px-3 py-2 text-sm sm:text-base text-foreground placeholder:text-foreground/35 focus:outline-none resize-none" />
          <div className="flex items-center justify-between px-2 pt-1">
            <span className="text-xs text-foreground/35">{idea.length}/{MAX}</span>
            <button type="submit" disabled={loading || !idea.trim()} data-testid="tav-submit"
              className="inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-5 py-2.5 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {tv.go}
            </button>
          </div>
        </form>

        {!data && !loading && !locked && (
          <div className="mb-6" data-testid="tav-presets">
            <p className="text-xs text-foreground/40 mb-2.5">{tv.suggest}</p>
            <div className="flex flex-wrap gap-2">
              {tv.presets.map((p, i) => (
                <button key={i} onClick={() => run(p)} data-testid={`tav-preset-${i}`}
                  className="text-xs sm:text-sm px-3.5 py-2 rounded-full border border-primary/20 text-foreground/70 hover:text-primary hover:bg-primary/[0.06] hover:border-primary/40 transition-colors">{p}</button>
              ))}
            </div>
          </div>
        )}

        {locked && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl border border-primary/25 p-8 text-center frame-pulse" data-testid="tav-locked">
            <span className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-4"><Lock size={24} strokeWidth={1.6} /></span>
            <p className="font-display text-xl text-foreground/90 mb-2">{tv.locked}</p>
            <p className="text-sm text-foreground/55 leading-relaxed max-w-md mx-auto mb-6">{tv.lockedDesc}</p>
            <button onClick={() => navigate("/tarifs")} data-testid="tav-locked-cta" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"><Sparkles size={15} /> {tv.lockedCta}</button>
          </motion.div>
        )}

        {loading && <AiThinking variant="verites" skeleton="cards" testId="tav-loading" />}

        <AnimatePresence>
          {data && !loading && (
            <motion.div key={data.idea} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="tav-result">
              <div className="text-center mb-2">
                <p className="uppercase tracking-[0.25em] text-[0.7rem] text-primary/70 mb-1.5">{tv.ideaLabel}</p>
                <p className="font-display italic text-lg sm:text-xl text-foreground/85 leading-snug">« {data.idea} »</p>
              </div>

              <div className="relative pl-6">
                <div aria-hidden className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-slate-400/40 via-red-400/40 to-primary/60" />
                {acts.map((a, i) => (
                  <motion.div key={a.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: i * 0.5 }}
                    className="relative mb-4" data-testid={`tav-act-${a.key}`}>
                    <span className={`absolute -left-6 top-4 w-3.5 h-3.5 rounded-full border-2 ${a.name} bg-[#0b0f18]`} style={{ borderColor: "currentColor" }} />
                    <div className={`rounded-2xl border p-5 ${a.cls}`}>
                      <p className={`font-display flex items-center gap-2.5 mb-2 ${a.name}`}>
                        <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${a.chip}`}><a.Icon size={17} /></span>
                        <span className="text-xs uppercase tracking-[0.2em] opacity-70">{i + 1}</span> {a.label}
                      </p>
                      <p className="text-sm sm:text-[0.95rem] text-foreground/80 leading-relaxed">{a.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {data.closing && (
                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.6 }}
                  className="glass rounded-2xl border border-primary/20 p-5 text-center stats-wow" data-testid="tav-closing">
                  <p className="uppercase tracking-[0.25em] text-[0.7rem] text-primary/70 mb-2 flex items-center justify-center gap-1.5"><Quote size={13} /> {tv.closingLabel}</p>
                  <p className="font-display italic text-base sm:text-lg text-foreground/85 leading-relaxed">{data.closing}</p>
                </motion.div>
              )}

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-1">
                <button onClick={deepen} data-testid="tav-deepen" className="btn-frost-orange inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"><Sparkles size={15} /> {tv.deepen}</button>
                <button onClick={shareCard} disabled={sharing} data-testid="tav-share" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-primary/90 border border-primary/30 hover:bg-primary/10 transition-colors disabled:opacity-50">
                  {sharing ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} {tv.card}
                </button>
                <button onClick={reset} data-testid="tav-again" className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm text-foreground/60 hover:text-primary border border-white/10 hover:border-primary/30 transition-colors"><RotateCcw size={15} /> {tv.again}</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
